import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, Clock, FolderTree } from "lucide-react";
import { createPageUrl } from "@/utils";
import { listVideos } from "@/api/content";
import { useRequireAuthRedirect } from "@/hooks/useRequireAuthRedirect";
import { useLanguage } from "@/components/ui/LanguageContext";
import { TAG_FILTER_DICTIONARY, getDictionaryLabel } from "@/config/catalogFilters";

export default function WatchHistoryPage() {
  const { canRenderProtectedContent, isLoadingAuth } = useRequireAuthRedirect();
  const { language } = useLanguage();

  const { data: videoResponse, isLoading } = useQuery({
    queryKey: ["watch-history-videos"],
    queryFn: async () => listVideos({ pageSize: 200 }),
    enabled: canRenderProtectedContent,
  });

  const videos = videoResponse?.items ?? [];

  const categoryRows = useMemo(() => {
    const map = new Map();
    for (const v of videos) {
      const firstTag = Array.isArray(v.tags) && v.tags.length > 0
        ? (typeof v.tags[0] === "string" ? v.tags[0] : v.tags[0]?.slug)
        : "other";
      const key = String(firstTag || "other");
      const row = map.get(key) ?? { key, count: 0, durationMinutes: 0 };
      row.count += 1;
      row.durationMinutes += Number(v.duration_minutes || 0);
      map.set(key, row);
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [videos]);

  if (isLoadingAuth || !canRenderProtectedContent) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#faf6f1] via-[#f5ede3] to-[#efe5d8] text-[#2d1810] pb-20">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        <Link
          to={createPageUrl("Home")}
          className="inline-flex items-center gap-1 text-sm text-[#6b5540] hover:text-[#3d2817] mb-6"
        >
          <ChevronLeft className="w-4 h-4" />
          ホームへ戻る
        </Link>

        <h1 className="text-2xl md:text-3xl font-bold mb-2">視聴履歴</h1>
        <p className="text-[#5d4529] mb-6">
          カテゴリー別の視聴状況（将来的に進捗データ連携予定）
        </p>

        <div className="rounded-xl border border-[#e8dccd] bg-white p-5 mb-6">
          <p className="text-sm text-[#6b5540]">全動画数</p>
          <p className="text-4xl font-bold text-[#2d1810] mt-1">{videos.length}</p>
        </div>

        <div className="rounded-xl border border-[#e8dccd] bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-[#efe5d8] font-semibold">カテゴリー別</div>
          {isLoading ? (
            <p className="px-4 py-6 text-[#6b5540]">Loading...</p>
          ) : categoryRows.length === 0 ? (
            <p className="px-4 py-6 text-[#6b5540]">データがありません</p>
          ) : (
            <ul className="divide-y divide-[#f1e7da]">
              {categoryRows.map((row) => (
                <li key={row.key} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      {getDictionaryLabel(TAG_FILTER_DICTIONARY, row.key, language, row.key)}
                    </p>
                    <p className="text-xs text-[#6b5540] inline-flex items-center gap-1 mt-1">
                      <FolderTree className="w-3.5 h-3.5" />
                      {row.count} 本
                    </p>
                  </div>
                  <p className="text-sm text-[#6b5540] inline-flex items-center gap-1 shrink-0">
                    <Clock className="w-3.5 h-3.5" />
                    {row.durationMinutes} 分
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
